import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { FormattedMessage } from 'react-intl';
import { usePageViewEvents as usePageViewEventsImport } from '../../../core/Analytics/usePageViewEvents';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { ProviderField } from '../../../models/provider';
import { SubmitProviderInputsPayload } from '../../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { Footer } from '../../atoms/Footer/Footer';
import { ApplicationEvent, PageView, UserInteractionEvent } from '../../../models/analytics';
import { Alert } from '../../molecules/Alert/Alert';
import { TemplateElementStyle, TemplateElementType, Templates } from '../templates';
import { MaybeChangeMethodButton } from '../MaybeChangeMethodButton/MaybeChangeMethodButton';
import { messages } from '../messages';
import { Button } from '../../atoms/Button/Button';
import { Text } from '../../atoms/Text/Text';
import { useCredentialsCopy } from '../../screens/SubmitCredentials/hooks/useCredentialsCopy';
import { H1 } from '../../atoms/Heading/Heading';
import { LogoSpinner } from '../../atoms/LogoSpinner/LogoSpinner';
import { FlexBox } from '../../atoms/FlexBox/FlexBox';

export type DecoupledTemplateScreenProps = {
  elements: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload, isAutoSubmit?: boolean) => void;
  usePageViewEvents?: typeof usePageViewEventsImport;
};

export const DecoupledTemplateScreen: React.FC<DecoupledTemplateScreenProps> = ({
  elements,
  onSubmit,
  usePageViewEvents = usePageViewEventsImport,
}) => {
  const { sendViewInteractionEvent } = usePageViewEvents({
    pageView: PageView.DECOUPLED_TEMPLATE_SCREEN,
  });

  const handleSubmitWithAnalytics = useCallback(
    (values: SubmitProviderInputsPayload, isAutoSubmit?: boolean) => {
      sendViewInteractionEvent({
        interaction: UserInteractionEvent.SUBMIT_SUPPLEMENTAL_INFORMATION,
      });
      onSubmit(values, isAutoSubmit);
    },
    [onSubmit, sendViewInteractionEvent]
  );

  const [hasAutoSubmitted, setHasAutoSubmitted] = useState<boolean>(false);
  const { tinklinkAnalytics } = useAnalytics();
  const { heading } = useCredentialsCopy();

  const noticeElement = elements.find((elm) => elm.type === TemplateElementType.NOTICE);

  const iconElement = elements.find((elm) => elm.type === TemplateElementType.ICON);
  const titleElement = elements.find(
    (elm) => elm.style === TemplateElementStyle.TITLE || elm.style === TemplateElementStyle.TEXT
  );
  const changeMethodElement = elements.find(
    (elm) => elm.type === TemplateElementType.CHANGE_METHOD
  );

  useEffect(() => {
    if (!hasAutoSubmitted && !changeMethodElement) {
      handleSubmitWithAnalytics({}, true);
      setHasAutoSubmitted(true);

      tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.DECOUPLED_AUTO_SUBMIT);
    }
  }, [
    handleSubmitWithAnalytics,
    hasAutoSubmitted,
    setHasAutoSubmitted,
    changeMethodElement,
    tinklinkAnalytics,
  ]);

  const handleUserHasAuthenticated = () => {
    handleSubmitWithAnalytics({});
    tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.DECOUPLED_USER_SUBMIT);
  };

  return (
    <ContentBody id={Templates.DECOUPLED} data-testid="decoupled-template-screen">
      {noticeElement ? (
        <Box mt="xxxlarge">
          <Alert title={noticeElement.value} body={[{ text: noticeElement.description }]} />
        </Box>
      ) : (
        <>
          <Box>
            <H1>{heading}</H1>
            {titleElement && (
              <Box data-testid="title">
                <Text color="secondary">{titleElement.value || ''}</Text>
              </Box>
            )}
          </Box>
          {iconElement && iconElement.value && (
            <FlexBox textAlign="center" direction="column" justifyContent="center" grow={1}>
              <LogoSpinner iconUrl={iconElement.value} />
            </FlexBox>
          )}
        </>
      )}
      <Footer stickToBottom>
        {changeMethodElement && (
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleUserHasAuthenticated}
          >
            <FormattedMessage {...messages.decoupledTemplateContinue} />
          </Button>
        )}
        <MaybeChangeMethodButton elements={elements} onSubmit={onSubmit} />
      </Footer>
    </ContentBody>
  );
};
